// @flow
import { getBidderId, getUserIsInGoodStanding, isUserLoggedIn } from '../../redux/modules/user';
import { getLiveCatalogData, getLiveItemData } from '../../redux/modules/console';
import { getUiLiveBid, submitLiveBid } from '../../redux/modules/liveBid';
import React, { Component, Fragment } from 'react';
import type { AlgoliaTransformedCardModel } from '../../types/AlgoliaTransformedCardModel';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { fetchLiveCatalogRegistrationIfNeeded } from '../../redux/modules/catalogRegistration';
import { fetchLiveCatalogStatusIfNeeded } from '../../redux/modules/liveCatalogStatus';
import { getBidLimitBalance } from '../../redux/modules/bidLimit';
import { getLiveItemStatus } from '../../redux/modules/liveItemStatus';
import ItemCard from './ItemCard';
import type { ItemCard as ItemCardType } from '../../types/ItemCard';
import { openRegisterForAuctionModal } from '../../redux/modules/modal';
import PubnubCatalogContainer from '../../components/PubnubContainer/PubnubCatalogContainer';
import { shouldShowLive } from '../../utils/live';

const mapStateToProps = (state, { allowBidding, cardViewModel, className, skipVisibilityCheck, trackClick }) => {
    const { catalog, item } = cardViewModel || {};
    const { absenteeBidders = 0, catalogId } = catalog || {};
    const { index, itemId } = item || {};
    const { bidders, currentItemIndex } = getLiveCatalogData(state, catalogId);
    const { bidCount, currentAskPrice, leadingBidder } = getLiveItemData(state, itemId);
    const accountInGoodStanding = getUserIsInGoodStanding(state);
    const bidderId = getBidderId(state);
    const bidLimitBalance = getBidLimitBalance(state, catalogId, itemId);
    const { catalogStatus, hasVideo, isCatalogOnly, saleStartTs } = catalog || {};
    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const { open: isLotOpen } = getLiveItemStatus(state, itemId);
    const showLive = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const userLoggedIn = isUserLoggedIn(state);
    const { submitted } = getUiLiveBid(state);

    return {
        accountInGoodStanding,
        allowBidding,
        bidCount,
        bidders: bidders + absenteeBidders,
        bidLimitBalance,
        cardViewModel,
        catalogId,
        className,
        comingUp,
        currentAskPrice,
        currentItemIndex,
        hasVideo,
        isLotOpen,
        leadingBidder: leadingBidder !== -1 && leadingBidder === bidderId,
        showLive,
        skipVisibilityCheck,
        submitted,
        trackClick,
        userLoggedIn,
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            fetchLiveCatalogRegistrationIfNeeded,
            fetchLiveCatalogStatusIfNeeded,
            openRegisterForAuctionModal,
            submitLiveBid,
        },
        dispatch
    );

type Props = {
    accountInGoodStanding: boolean,
    allowBidding: boolean,
    bidCount: number,
    bidders: number,
    bidLimitBalance: number,
    cardViewModel: ItemCardType | AlgoliaTransformedCardModel,
    catalogId: number,
    className: string,
    comingUp: number,
    currentAskPrice: number,
    currentItemIndex: number,
    fetchLiveCatalogRegistrationIfNeeded: Function,
    fetchLiveCatalogStatusIfNeeded: Function,
    hasVideo: boolean,
    hideLiveButton: boolean,
    hideLotNumbers: boolean,
    hideSeller: boolean,
    isLotOpen: boolean,
    key: number,
    last: boolean,
    leadingBidder: boolean,
    noPubNub: boolean,
    noWidthLimit: boolean,
    openAbsenteeBidModal: Function,
    openOnBlank: boolean,
    openRegisterForAuctionModal: Function,
    openRetractBidModal: Function,
    openSendSellerMessageModal: Function,
    showBanner: boolean,
    showBidStatus: boolean,
    showCta: boolean,
    showDate: boolean,
    showEstimate: boolean,
    showImageWithHover: boolean,
    showLive: boolean,
    skipVisibilityCheck: boolean,
    style: any,
    submitLiveBid: Function,
    submitted: boolean,
    trackClick: Function,
    userLoggedIn: boolean,
};

class ItemCardContainer extends Component<Props> {
    componentDidMount() {
        const {
            allowBidding,
            cardViewModel,
            catalogId,
            fetchLiveCatalogRegistrationIfNeeded,
            fetchLiveCatalogStatusIfNeeded,
        } = this.props;
        if (cardViewModel?.catalog?.catalogStatus === 'live') {
            allowBidding && fetchLiveCatalogStatusIfNeeded(catalogId);
            fetchLiveCatalogRegistrationIfNeeded(catalogId);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            allowBidding,
            cardViewModel,
            catalogId,
            fetchLiveCatalogRegistrationIfNeeded,
            fetchLiveCatalogStatusIfNeeded,
        } = this.props;

        if (
            prevProps.cardViewModel?.catalog?.catalogStatus !== 'live' &&
            cardViewModel?.catalog?.catalogStatus === 'live'
        ) {
            allowBidding && fetchLiveCatalogStatusIfNeeded(catalogId);
            fetchLiveCatalogRegistrationIfNeeded(catalogId);
        }
    }

    render() {
        const {
            accountInGoodStanding,
            allowBidding,
            bidCount,
            bidders,
            bidLimitBalance,
            cardViewModel,
            catalogId,
            className,
            comingUp,
            currentAskPrice,
            currentItemIndex,
            hasVideo,
            hideLiveButton,
            hideLotNumbers,
            hideSeller,
            key,
            last,
            leadingBidder,
            noPubNub,
            noWidthLimit,
            openAbsenteeBidModal,
            openOnBlank,
            openRetractBidModal,
            openSendSellerMessageModal,
            showBanner,
            showBidStatus,
            showCta,
            showDate,
            showEstimate,
            showImageWithHover,
            showLive,
            skipVisibilityCheck,
            style,
            submitLiveBid,
            submitted,
            trackClick,
            userLoggedIn,
        } = this.props;

        return (
            <Fragment>
                {comingUp && allowBidding && !noPubNub && (
                    <PubnubCatalogContainer catalogId={catalogId} limitMessagesFilter="itemCard" />
                )}
                <ItemCard
                    accountInGoodStanding={accountInGoodStanding}
                    allowBidding={allowBidding}
                    bidCount={bidCount}
                    bidders={bidders}
                    bidLimitBalance={bidLimitBalance}
                    cardViewModel={cardViewModel}
                    className={className}
                    currentAskPrice={currentAskPrice}
                    currentItemIndex={currentItemIndex}
                    comingUp={comingUp}
                    hasVideo={hasVideo}
                    hideLiveButton={hideLiveButton}
                    hideLotNumbers={hideLotNumbers}
                    hideSeller={hideSeller}
                    key={key}
                    last={last}
                    leadingBidder={leadingBidder}
                    noWidthLimit={noWidthLimit}
                    openAbsenteeBidModal={openAbsenteeBidModal}
                    openOnBlank={openOnBlank}
                    openRetractBidModal={openRetractBidModal}
                    openRegisterForAuctionModal={this.props.openRegisterForAuctionModal}
                    openSendSellerMessageModal={openSendSellerMessageModal}
                    showBanner={showBanner}
                    showBidStatus={showBidStatus}
                    showCta={showCta}
                    showDate={showDate}
                    showEstimate={showEstimate}
                    showImageWithHover={showImageWithHover}
                    showLive={showLive}
                    showSocial={allowBidding && showLive}
                    skipVisibilityCheck={skipVisibilityCheck}
                    style={style}
                    submitLiveBid={submitLiveBid}
                    submitted={submitted}
                    trackClick={trackClick}
                    userLoggedIn={userLoggedIn}
                />
            </Fragment>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ItemCardContainer);
